import React, { useEffect, useRef, useState } from "react";
import { useHtmlByUrl } from "../../common/html_artifact_store.es6";
import { registerSeek, unregisterSeek } from "../../_visor/utils/recorderSeekRegistry";

/**
 * MotionScene playback.
 *
 * The scene itself is a self-contained HTML document in blob storage, rendered
 * inside a sandboxed iframe (`allow-scripts`, no `allow-same-origin`, so it
 * runs in an opaque origin and cannot reach the editor's DOM, cookies or
 * storage). It has NO CLOCK: it only ever draws the frame it is told to.
 *
 *   host   → { type: 'motion:seek', t, seq, subframes }
 *   scene  → draws, then { type: 'motion:seeked', seq }
 *   scene  → { type: 'motion:ready', durationSec }   (once, on load)
 *
 * TIME SOURCE, in priority order — the scene never runs free:
 *
 *  1. Recorder. `recorderSeekRegistry` hands us a box-relative time and AWAITS
 *     our promise before screenshotting, so we resolve only once the scene has
 *     acknowledged the frame. This is the whole reason the handshake is
 *     sequence-numbered: an unacknowledged seek must stall the capture loop,
 *     not let it photograph a stale frame.
 *  2. Editor playhead — `aidea:char-time` ticks (box-relative seconds) from the
 *     editor timeline. Paused → no ticks → the scene holds its last frame.
 *  3. Live visor playback — wall-clock from mount, paused when
 *     `visorPlaying === false`. Only for real end-user viewing.
 *  4. Otherwise (editor at rest) — frozen at t=0.
 *
 * MOTION BLUR is requested per frame: 1 sample during live playback, 6 while
 * the recorder is capturing a frame it will keep. Blur costs one extra draw
 * per sample, which is affordable offline and is not at 60 fps in an editor.
 */

const EXPORT_SUBFRAMES = 6;
const READY_TIMEOUT_MS = 20000;
const SEEK_TIMEOUT_MS = 15000;

export default function MotionScenePlayback({
  state,
  boxId,
  visor,
  visorCurrentTime,
  visorPlaying,
}) {
  const htmlUrl = (state && state.htmlUrl) || "";
  const duration = (state && state.durationSec) || 0;

  const frameRef = useRef(null);
  const readyRef = useRef(false);
  const readyWaiters = useRef([]);
  const pending = useRef(new Map());
  const seqRef = useRef(0);
  // Set as soon as anything outside drives us (recorder or editor playhead);
  // the wall-clock loop stands down so two clocks never fight over one frame.
  const hostDriven = useRef(false);

  // Announce to the editor's chat that a motion scene is on screen.
  //
  // Without it the agent has no idea what it is looking at: with nothing
  // selected the selection descriptor is empty, and "make this house's roof
  // red" routes to the image editor on a scene that holds no images. The
  // plugin being mounted is the most direct evidence there is, so it is the
  // thing that says so.
  useEffect(() => {
    if (typeof window === "undefined" || visor) return undefined;
    window.__aideaMotionScene = { boxId, durationSec: duration };
    return () => {
      // Only clear our own: two motion boxes never coexist on a scene, but a
      // scene change unmounts one and mounts the next in either order.
      if (window.__aideaMotionScene && window.__aideaMotionScene.boxId === boxId) {
        window.__aideaMotionScene = undefined;
      }
    };
  }, [boxId, duration, visor]);

  // The gate serves HTML as an attachment and forbids framing, so the document
  // is fetched with the Bearer token and handed over as srcdoc (cached per URL).
  // `sandbox="allow-scripts"` without `allow-same-origin` still puts it in an
  // opaque origin; messages are matched by `e.source`, never by origin.
  const { html, failed: fetchFailed, directSrc } = useHtmlByUrl(htmlUrl);
  const [failed, setFailed] = useState(false);

  // ── the scene's side of the protocol ──────────────────────────────────
  useEffect(() => {
    const onMessage = (e) => {
      const frame = frameRef.current;
      if (!frame || e.source !== frame.contentWindow) return;
      const d = e.data || {};

      // ANY message means the scene is alive. The failure notice is driven by
      // a timeout, and only motion:ready used to clear it — so a scene that
      // merely loaded slowly kept the notice forever, drawing correctly with
      // "could not load" sitting on top of it.
      setFailed(false);

      if (d.type === "motion:ready") {
        readyRef.current = true;
        readyWaiters.current.splice(0).forEach((fn) => fn());
        return;
      }
      if (d.type === "motion:seeked") {
        // A reply is proof of life, so it counts as ready too.
        //
        // motion:ready CAN BE MISSED. The document announces it the moment it
        // loads, and a cached document loads in ~0 ms — before React has
        // attached this listener (StrictMode also detaches and re-attaches it
        // once, leaving a second gap). Nothing else ever cleared readyRef, so
        // the box sat behind "could not load" over a perfectly good scene.
        // The approval card previews the same URL, which warms the cache for
        // every generation, so this was systematic rather than rare.
        readyRef.current = true;
        readyWaiters.current.splice(0).forEach((fn) => fn());
        const p = pending.current.get(d.seq);
        if (p) {
          pending.current.delete(d.seq);
          p.resolve();
        }
        return;
      }
      if (d.type === "motion:error") {
        // eslint-disable-next-line no-console
        console.warn("[MotionScene] scene error:", d.message);
        const p = pending.current.get(d.seq);
        if (p) {
          pending.current.delete(d.seq);
          // Resolve rather than reject: one bad frame must not abort an export.
          p.resolve();
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const waitForReady = () =>
    new Promise((resolve) => {
      if (readyRef.current) return resolve();
      readyWaiters.current.push(resolve);
      setTimeout(resolve, READY_TIMEOUT_MS);
      return undefined;
    });

  // An iframe does not reliably fire onError for a 403 or a missing blob, so
  // the only honest signal that the scene never loaded is that it never said
  // motion:ready.
  useEffect(() => {
    if (!htmlUrl) return undefined;
    setFailed(false);
    const timer = setTimeout(() => {
      if (!readyRef.current) setFailed(true);
    }, READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [htmlUrl]);

  const seek = (tSec, subframes) =>
    new Promise((resolve) => {
      const frame = frameRef.current;
      if (!frame || !frame.contentWindow) return resolve();
      const id = ++seqRef.current;
      pending.current.set(id, { resolve });
      frame.contentWindow.postMessage(
        { type: "motion:seek", t: tSec, seq: id, subframes: subframes || 1 },
        "*"
      );
      // A lost reply must not wedge playback or hang the capture loop.
      setTimeout(() => {
        if (pending.current.has(id)) {
          pending.current.delete(id);
          resolve();
        }
      }, SEEK_TIMEOUT_MS);
      return undefined;
    });

  // ── 1. recorder ───────────────────────────────────────────────────────
  // Registered on mount, BEFORE the document can load: runAllSeeks awaits
  // every handler, so a box that mounts mid-scene stalls the recorder until
  // it can paint instead of being captured blank.
  useEffect(() => {
    if (!boxId) return undefined;
    const handler = async (tSec) => {
      hostDriven.current = true;
      await waitForReady();
      await seek(tSec, EXPORT_SUBFRAMES);
    };
    registerSeek(boxId, handler);
    return () => unregisterSeek(boxId);
  }, [boxId]);

  // ── 2. editor playhead ────────────────────────────────────────────────
  useEffect(() => {
    if (!boxId) return undefined;
    const onTick = (e) => {
      const d = e && e.detail;
      if (!d || d.layerId !== boxId) return;
      if (typeof visorCurrentTime === "number") return; // recorder owns time
      hostDriven.current = true;
      const t = d.inRange === false ? 0 : typeof d.t === "number" ? d.t : 0;
      seek(t, 1);
    };
    window.addEventListener("aidea:char-time", onTick);
    return () => window.removeEventListener("aidea:char-time", onTick);
  }, [boxId, visorCurrentTime]);

  // ── 1b. recorder, prop form ───────────────────────────────────────────
  useEffect(() => {
    if (typeof visorCurrentTime !== "number") return;
    hostDriven.current = true;
    seek(visorCurrentTime, EXPORT_SUBFRAMES);
  }, [visorCurrentTime]);

  // ── 3. live visor playback ────────────────────────────────────────────
  const live = visor === true && typeof visorCurrentTime !== "number";
  useEffect(() => {
    if (!live || visorPlaying === false || !htmlUrl) return undefined;
    let raf = null;
    let start = null;
    let stopped = false;
    const loop = (ts) => {
      if (stopped) return;
      if (hostDriven.current) return; // the editor/recorder took over
      if (start == null) start = ts;
      let elapsed = (ts - start) / 1000;
      if (duration > 0 && elapsed > duration) {
        start = ts;
        elapsed = 0;
      }
      seek(elapsed, 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [live, visorPlaying, htmlUrl, duration]);

  // ── 4. at rest ────────────────────────────────────────────────────────
  // The editor before any play or scrub. t=0 is usually a fade-in, which would
  // show an empty box, so hold a frame a little way in — enough that the
  // author sees what they made.
  useEffect(() => {
    if (!htmlUrl || hostDriven.current || live) return;
    let cancelled = false;
    waitForReady().then(() => {
      if (!cancelled && !hostDriven.current) {
        seek(Math.min(duration * 0.35, 3), 1);
      }
    });
    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [htmlUrl, live, duration]);

  const wrap = {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    overflow: "hidden",
    background: "#000",
  };

  // The iframe stays mounted even while `failed` is showing. Replacing it with
  // the message would destroy the document, so a scene that was merely slow
  // could never recover — the notice is an overlay, and a late motion:ready
  // clears it.
  return (
    <div style={wrap}>
      <iframe
        ref={frameRef}
        title="motion-scene"
        // No allow-same-origin: the scene runs in an opaque origin and cannot
        // touch the editor. Everything it needs arrives by postMessage.
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        srcDoc={directSrc ? undefined : html || ""}
        src={directSrc || undefined}
        // Yüklenir yüklenmez bir kare iste. El sıkışmanın ikinci yarısı bu:
        // dokümanın "hazırım" duyurusunu kaçırsak bile cevabı buradan alıyoruz
        // (cevap da ready sayılıyor, yukarıdaki nota bakın) ve ilk kare
        // playhead beklemeden çiziliyor.
        onLoad={() => {
          if (!html && !directSrc) return;
          seek(typeof visorCurrentTime === "number" ? visorCurrentTime : 0, 1);
        }}
        style={{
          width: "100%",
          height: "100%",
          border: 0,
          display: "block",
          // EDİTÖRDE fareyi almaz. Sahnede tıklanacak bir şey yok — zamanı
          // playhead sürüyor, doküman kendi saatini kurmuyor — ama iframe
          // fareyi yutunca kutu ORTASINDAN sürüklenemiyor: boyutlandırma
          // kenardaki tutamaklardan gittiği için çalışıyor, taşıma hiç
          // başlamıyordu. Motionize'ın kutusu zaten tam ekran ve sabit olduğu
          // için bu fark orada görünmüyordu; Görsel Ekle'den gelen motion
          // kutusu ise sahneye yerleştirilen bir öğe.
          pointerEvents: visor ? "auto" : "none",
        }}
      />
      {(failed || fetchFailed) && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <span style={{ color: "#c88", font: "12px system-ui, sans-serif" }}>
            Motion sahnesi yüklenemedi
          </span>
        </div>
      )}
    </div>
  );
}
